import React from 'react';
import { Box, Button, Typography, Select, MenuItem, FormControl } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

export default function PaginationBar({
    currentPage = 1,
    totalPages = 1,
    totalRows = 0,
    rowsPerPage = 10,
    onPageChange,
    onRowsPerPageChange,
    showRowsPerPage = true,
    rowsPerPageOptions = [10, 25, 50, 100],
}) {
    const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }
        if (startPage > 1) {
            pages.push(1);
            if (startPage > 2) pages.push('...');
        }
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                    Showing {start} to {end} of {totalRows}
                </Typography>
                {showRowsPerPage && onRowsPerPageChange && (
                    <FormControl size="small">
                        <Select
                            value={rowsPerPage}
                            onChange={(e) => onRowsPerPageChange(e.target.value)}
                            sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px', height: 32, minWidth: 70, bgcolor: '#fff' }}
                        >
                            {rowsPerPageOptions.map((n) => (
                                <MenuItem key={n} value={n} sx={{ fontFamily, fontSize: '0.82rem' }}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                    size="small"
                    disabled={currentPage <= 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    sx={{ minWidth: 32, p: 0.5, color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                >
                    <NavigateBeforeIcon fontSize="small" />
                </Button>
                {getPageNumbers().map((page, idx) =>
                    page === '...' ? (
                        <Typography key={`ellipsis-${idx}`} sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', px: 0.5 }}>...</Typography>
                    ) : (
                        <Button
                            key={page}
                            size="small"
                            onClick={() => onPageChange(page)}
                            sx={{
                                minWidth: 32, p: 0.5, fontFamily, fontSize: '0.8rem', fontWeight: 600, borderRadius: '8px',
                                ...(page === currentPage
                                    ? { bgcolor: GREEN_ACCENT, color: '#fff', '&:hover': { bgcolor: '#059669' } }
                                    : { color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }),
                            }}
                        >
                            {page}
                        </Button>
                    )
                )}
                <Button
                    size="small"
                    disabled={currentPage >= totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    sx={{ minWidth: 32, p: 0.5, color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                >
                    <NavigateNextIcon fontSize="small" />
                </Button>
            </Box>
        </Box>
    );
}
